import type { CarAd, MobilityAd } from "@mobility-item-page/types";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import type { FeatureToggles } from "src/types/unleash.ts";
import { FeatureIds } from "src/types/unleash.ts";
import { ChargingCostCard } from "./ChargingCostCard.tsx";
import { EvGuidesCard } from "./EvGuidesCard.tsx";
import { RangeAndBatteryCard } from "./RangeAndBatteryCard.tsx";

/**
 * Whether the ad shows the EV info section at all. Also gates the contact form prefill, so a stale
 * `?contact=<topic>` link cannot ask a battery question on an ad that never offered one.
 */
export const showsEvInfoSection = (ad: MobilityAd, featureToggles: FeatureToggles): ad is CarAd =>
  Boolean(featureToggles[FeatureIds.ENABLE_EV_INFO_SECTION]) && "ev_info_links" in ad && Boolean(ad.ev_info_links);

export const EvInfoSection = () => {
  const ad = useAdViewAd();
  const { featureToggles } = useAppConfig();

  if (!showsEvInfoSection(ad, featureToggles)) {
    return null;
  }

  return (
    // The gap to the key info list below is a bottom margin here rather than a top margin on
    // `.key-info`, because this card is absent on most ads. A top margin on `.key-info` would
    // then stack on top of the specifications area's own `pt-40` and double the gap.
    <div
      data-testid="ev-info"
      className="mb-40 p-16 flex flex-col gap-16 s-bg-info-subtle dark:s-bg-subtle -mx-16 md:mx-0 md:rounded-8"
    >
      <RangeAndBatteryCard ad={ad} />
      <ChargingCostCard ad={ad} />
      <EvGuidesCard ad={ad} />
    </div>
  );
};
